import * as React from 'react';

import { refreshDataSourceTables } from 'src/actions/data-sources/action-creators/refreshDataSourceTables';
import { connectionSelector } from 'src/selectors/connection';
import { isConnectedSelector } from 'src/selectors/isConnected';
import { setupPortalConnection } from 'src/store/connections/connectToStardog';
import { coercePortalConnection } from 'src/store/connections/types';
import { ProjectVirtualGraph } from 'src/store/state/projects';
import { useDefaultPortalConnection } from 'src/utils/hooks/useDefaultPortalConnection';
import { useDidValueChange } from 'src/utils/hooks/useDidValueChange';
import { useReduxDispatch } from 'src/utils/hooks/useReduxDispatch';
import { useReduxSelector } from 'src/utils/hooks/useReduxSelector';

export const useRefreshVgTables = (
  virtualGraph: ProjectVirtualGraph | null
) => {
  const dispatch = useReduxDispatch();
  const defaultPortalConnection = useDefaultPortalConnection();
  const currentConnection = useReduxSelector(connectionSelector);
  const isConnected = useReduxSelector(isConnectedSelector);
  const isCurrentConnectionIncorrect =
    !currentConnection ||
    (virtualGraph &&
      (!virtualGraph.connectionId ||
        virtualGraph.connectionId !== currentConnection.id));

  const didConnection = React.useRef(false);
  const didRefresh = React.useRef(false);
  const didDataSourceChange = useDidValueChange(
    virtualGraph?.dataSourceEndpoint,
    (a, b) => !a || !b || a === b
  );

  React.useEffect(() => {
    if (
      currentConnection ||
      (didConnection.current && !isCurrentConnectionIncorrect) ||
      !defaultPortalConnection
    ) {
      return;
    }
    didConnection.current = true;

    dispatch(
      setupPortalConnection(coercePortalConnection(defaultPortalConnection))
    );
  }, [
    currentConnection,
    defaultPortalConnection,
    didConnection,
    dispatch,
    isCurrentConnectionIncorrect,
  ]);

  React.useEffect(() => {
    if (didDataSourceChange) {
      didRefresh.current = false;
    }
  }, [didDataSourceChange]);

  React.useEffect(() => {
    if (didRefresh.current || !isConnected || !virtualGraph?.dataSourceId) {
      return;
    }
    didRefresh.current = true;
    dispatch(refreshDataSourceTables(virtualGraph.dataSourceId));
  }, [currentConnection, dispatch, isConnected, virtualGraph]);
};
